import { useEffect, useState, useMemo } from "react";
import { Plus, Pencil, Trash2, Building2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { api, apiErrorMessage } from "@/lib/api";

export default function AdminOrganisasi() {
  return (
    <div className="p-4 sm:p-8 max-w-6xl mx-auto" data-testid="page-organisasi">
      <div>
        <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-teal-700">Administrasi</div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 mt-1 leading-tight">
          Organisasi
        </h1>
        <p className="text-slate-500 text-sm mt-1">
          Kelola Divisi, Unit Kerja, dan Jabatan.
        </p>
      </div>

      <Tabs defaultValue="divisi" className="mt-6">
        <TabsList className="bg-white border border-slate-200 h-11">
          <TabsTrigger value="divisi" data-testid="tab-divisi" className="data-[state=active]:bg-teal-700 data-[state=active]:text-white">
            Divisi
          </TabsTrigger>
          <TabsTrigger value="unit" data-testid="tab-unit" className="data-[state=active]:bg-teal-700 data-[state=active]:text-white">
            Unit Kerja
          </TabsTrigger>
          <TabsTrigger value="jabatan" data-testid="tab-jabatan" className="data-[state=active]:bg-teal-700 data-[state=active]:text-white">
            Jabatan
          </TabsTrigger>
        </TabsList>

        <TabsContent value="divisi" className="mt-6">
          <DivisiPanel />
        </TabsContent>
        <TabsContent value="unit" className="mt-6">
          <UnitPanel />
        </TabsContent>
        <TabsContent value="jabatan" className="mt-6">
          <JabatanPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}

/* ------------------------ Divisi ------------------------ */

function DivisiPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ nama: "", kode: "", deskripsi: "" });

  async function load() {
    setLoading(true);
    try { const { data } = await api.get("/master/divisi"); setRows(data); }
    catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function save() {
    try {
      if (editing) await api.put(`/master/divisi/${editing.id}`, form);
      else await api.post("/master/divisi", form);
      toast.success("Berhasil disimpan");
      setOpen(false); load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  }
  async function del(r) {
    if (!confirm(`Hapus divisi ${r.nama}?`)) return;
    try { await api.delete(`/master/divisi/${r.id}`); toast.success("Dihapus"); load(); }
    catch (e) { toast.error(apiErrorMessage(e)); }
  }

  return (
    <>
      <div className="flex justify-end mb-4">
        <Button onClick={() => { setEditing(null); setForm({ nama: "", kode: "", deskripsi: "" }); setOpen(true); }}
          className="bg-teal-700 hover:bg-teal-800 gap-2" data-testid="divisi-add-btn">
          <Plus size={14} /> Tambah Divisi
        </Button>
      </div>
      <SimpleTable
        loading={loading} rows={rows} emptyLabel="Belum ada divisi"
        cols={[
          { h: "Kode", cell: (r) => <span className="font-mono-clock text-xs">{r.kode || "—"}</span> },
          { h: "Nama", cell: (r) => <span className="font-semibold text-slate-900">{r.nama}</span> },
          { h: "Deskripsi", cell: (r) => <span className="text-slate-600 text-sm">{r.deskripsi || "—"}</span> },
        ]}
        onEdit={(r) => { setEditing(r); setForm({ nama: r.nama, kode: r.kode || "", deskripsi: r.deskripsi || "" }); setOpen(true); }}
        onDelete={del}
        testPrefix="divisi"
      />
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="divisi-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Divisi" : "Tambah Divisi"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div><Label>Nama</Label><Input className="mt-1.5" value={form.nama} onChange={(e) => setForm({ ...form, nama: e.target.value })} data-testid="divisi-input-nama" /></div>
            <div><Label>Kode</Label><Input className="mt-1.5" value={form.kode} onChange={(e) => setForm({ ...form, kode: e.target.value })} data-testid="divisi-input-kode" /></div>
            <div><Label>Deskripsi</Label><Textarea className="mt-1.5" value={form.deskripsi} onChange={(e) => setForm({ ...form, deskripsi: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button className="bg-teal-700 hover:bg-teal-800" onClick={save} data-testid="divisi-save-btn">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/* ------------------------ Unit ------------------------ */

function UnitPanel() {
  const [rows, setRows] = useState([]);
  const [divisi, setDivisi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ nama: "", kode: "", deskripsi: "", divisi_id: "" });

  const divisiMap = useMemo(() => Object.fromEntries(divisi.map((d) => [d.id, d.nama])), [divisi]);

  async function load() {
    setLoading(true);
    try {
      const [u, d] = await Promise.all([api.get("/master/unit"), api.get("/master/divisi")]);
      setRows(u.data); setDivisi(d.data);
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function save() {
    if (!form.divisi_id) { toast.error("Pilih Divisi"); return; }
    try {
      if (editing) await api.put(`/master/unit/${editing.id}`, form);
      else await api.post("/master/unit", form);
      toast.success("Berhasil disimpan"); setOpen(false); load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  }
  async function del(r) {
    if (!confirm(`Hapus unit ${r.nama}?`)) return;
    try { await api.delete(`/master/unit/${r.id}`); toast.success("Dihapus"); load(); }
    catch (e) { toast.error(apiErrorMessage(e)); }
  }

  return (
    <>
      <div className="flex justify-end mb-4">
        <Button onClick={() => { setEditing(null); setForm({ nama: "", kode: "", deskripsi: "", divisi_id: "" }); setOpen(true); }}
          className="bg-teal-700 hover:bg-teal-800 gap-2" data-testid="unit-add-btn">
          <Plus size={14} /> Tambah Unit
        </Button>
      </div>
      <SimpleTable
        loading={loading} rows={rows} emptyLabel="Belum ada unit"
        cols={[
          { h: "Kode", cell: (r) => <span className="font-mono-clock text-xs">{r.kode || "—"}</span> },
          { h: "Nama Unit", cell: (r) => <span className="font-semibold text-slate-900">{r.nama}</span> },
          { h: "Divisi", cell: (r) => <span className="text-slate-600 text-sm">{divisiMap[r.divisi_id] || "—"}</span> },
        ]}
        onEdit={(r) => { setEditing(r); setForm({ nama: r.nama, kode: r.kode || "", deskripsi: r.deskripsi || "", divisi_id: r.divisi_id || "" }); setOpen(true); }}
        onDelete={del}
        testPrefix="unit"
      />
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="unit-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Unit" : "Tambah Unit"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div><Label>Nama Unit</Label><Input className="mt-1.5" value={form.nama} onChange={(e) => setForm({ ...form, nama: e.target.value })} data-testid="unit-input-nama" /></div>
            <div><Label>Kode</Label><Input className="mt-1.5" value={form.kode} onChange={(e) => setForm({ ...form, kode: e.target.value })} /></div>
            <div>
              <Label>Divisi</Label>
              <Select value={form.divisi_id} onValueChange={(v) => setForm({ ...form, divisi_id: v })}>
                <SelectTrigger className="mt-1.5" data-testid="unit-input-divisi"><SelectValue placeholder="Pilih divisi" /></SelectTrigger>
                <SelectContent>{divisi.map((d) => (<SelectItem key={d.id} value={d.id}>{d.nama}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div><Label>Deskripsi</Label><Textarea className="mt-1.5" value={form.deskripsi} onChange={(e) => setForm({ ...form, deskripsi: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button className="bg-teal-700 hover:bg-teal-800" onClick={save} data-testid="unit-save-btn">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/* ------------------------ Jabatan ------------------------ */

function JabatanPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ nama: "", level: "", deskripsi: "" });

  async function load() {
    setLoading(true);
    try { const { data } = await api.get("/master/jabatan"); setRows(data); }
    catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function save() {
    try {
      if (editing) await api.put(`/master/jabatan/${editing.id}`, form);
      else await api.post("/master/jabatan", form);
      toast.success("Berhasil disimpan"); setOpen(false); load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  }
  async function del(r) {
    if (!confirm(`Hapus jabatan ${r.nama}?`)) return;
    try { await api.delete(`/master/jabatan/${r.id}`); toast.success("Dihapus"); load(); }
    catch (e) { toast.error(apiErrorMessage(e)); }
  }

  return (
    <>
      <div className="flex justify-end mb-4">
        <Button onClick={() => { setEditing(null); setForm({ nama: "", level: "", deskripsi: "" }); setOpen(true); }}
          className="bg-teal-700 hover:bg-teal-800 gap-2" data-testid="jabatan-add-btn">
          <Plus size={14} /> Tambah Jabatan
        </Button>
      </div>
      <SimpleTable
        loading={loading} rows={rows} emptyLabel="Belum ada jabatan"
        cols={[
          { h: "Nama Jabatan", cell: (r) => <span className="font-semibold text-slate-900">{r.nama}</span> },
          { h: "Level", cell: (r) => <span className="text-slate-600 text-sm">{r.level || "—"}</span> },
          { h: "Deskripsi", cell: (r) => <span className="text-slate-600 text-sm">{r.deskripsi || "—"}</span> },
        ]}
        onEdit={(r) => { setEditing(r); setForm({ nama: r.nama, level: r.level || "", deskripsi: r.deskripsi || "" }); setOpen(true); }}
        onDelete={del}
        testPrefix="jabatan"
      />
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="jabatan-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Jabatan" : "Tambah Jabatan"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div><Label>Nama</Label><Input className="mt-1.5" value={form.nama} onChange={(e) => setForm({ ...form, nama: e.target.value })} data-testid="jabatan-input-nama" /></div>
            <div><Label>Level</Label><Input className="mt-1.5" value={form.level} onChange={(e) => setForm({ ...form, level: e.target.value })} placeholder="staff / manager / direksi" /></div>
            <div><Label>Deskripsi</Label><Textarea className="mt-1.5" value={form.deskripsi} onChange={(e) => setForm({ ...form, deskripsi: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button className="bg-teal-700 hover:bg-teal-800" onClick={save} data-testid="jabatan-save-btn">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/* ------------------------ Shared Table ------------------------ */

function SimpleTable({ loading, rows, cols, emptyLabel, onEdit, onDelete, testPrefix }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50 hover:bg-slate-50">
              {cols.map((c) => (
                <TableHead key={c.h} className="uppercase text-[10px] tracking-widest font-bold text-slate-500">{c.h}</TableHead>
              ))}
              <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500 text-right">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={cols.length + 1} className="text-center py-10 text-slate-500">Memuat…</TableCell></TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={cols.length + 1} className="text-center py-14">
                  <div className="mx-auto w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center text-slate-400">
                    <Building2 size={22} />
                  </div>
                  <div className="mt-3 font-semibold text-slate-700">{emptyLabel}</div>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((r) => (
                <TableRow key={r.id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`${testPrefix}-row-${r.id}`}>
                  {cols.map((c) => <TableCell key={c.h}>{c.cell(r)}</TableCell>)}
                  <TableCell className="text-right">
                    <button onClick={() => onEdit(r)} className="inline-flex items-center gap-1 text-xs font-semibold text-teal-800 hover:text-teal-900" data-testid={`${testPrefix}-edit-${r.id}`}>
                      <Pencil size={13} /> Edit
                    </button>
                    {/* Tombol Hapus dihilangkan (Keamanan Data — Feb 2026). */}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
